import { useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MAX_BULK_INVITES, type CreateInviteInput } from '@proref/schemas';
import type { UserRole } from '@proref/types';
import { Modal } from '../shared/Modal';
import { Field } from '../shared/Field';
import { Input } from '../shared/Input';
import { Select } from '../shared/Select';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { Icon } from '../shared/Icons';
import { useBulkInvite } from '../../hooks/use-invites';
import { useFederationsQuery } from '../../hooks/use-federations';
import { useSportsQuery } from '../../hooks/use-sports';
import { useAuth } from '../../contexts/auth-context';
import { checkInviteRow, parseRosterPaste } from '../../lib/roster-paste';
import { notifyError, notifySuccess, notifyWarning } from '../../lib/toast';
import type { BulkInviteRowResult } from '../../services/invites.service';

// One line of the form. `key` is stable per row so React doesn't reuse an input's DOM node for a
// different person when one is removed from the middle (which would carry the wrong value over).
interface DraftRow {
  key: string;
  full_name: string;
  email: string;
}

const STARTING_ROWS = 3;

let rowSeq = 0;
function emptyRow(): DraftRow {
  rowSeq += 1;
  return { key: `row-${rowSeq}`, full_name: '', email: '' };
}

// Invite several people at once. Two labelled fields per person rather than a paste box: this is
// mostly used to add a handful of colleagues by hand, and a free-text format makes you think about
// separators and column order to type three names.
//
// A whole roster pasted from a spreadsheet is still worth supporting, so a multi-line paste into
// any field EXPANDS into rows (and "Add a CSV" fills them the same way) — but what you get back is
// the same editable fields, correctable before anything sends, not a blob to re-read.
export function BulkInviteDialog() {
  const { t } = useTranslation();
  const { user: me, role } = useAuth();
  const isSuper = role === 'super_admin';
  const bulk = useBulkInvite();

  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<DraftRow[]>(() => Array.from({ length: STARTING_ROWS }, emptyRow));
  const [inviteRole, setInviteRole] = useState<Exclude<UserRole, 'super_admin'>>('referee');
  const [federationId, setFederationId] = useState('');
  const [sportId, setSportId] = useState('');
  const [results, setResults] = useState<BulkInviteRowResult[] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // A super_admin has no tenant of its own, so it must name where the whole batch lands; a
  // federation_admin's comes from the JWT and these are never fetched.
  const { data: federations } = useFederationsQuery(isSuper && open);
  const { data: sports } = useSportsQuery(isSuper && open);

  // A row nobody has touched is not an error — it's the next empty line. Only rows carrying
  // something are judged, so the form can sit there with three blanks and still be "valid".
  const filled = useMemo(() => rows.filter((r) => r.full_name.trim() || r.email.trim()), [rows]);

  // Validated + de-duplicated, keyed back to the row so each message sits under its own field.
  const checked = useMemo(() => {
    const seen = new Set<string>();
    return filled.map((row) => {
      const result = checkInviteRow(row.full_name, row.email);
      if (!result.error && seen.has(result.email)) {
        return { key: row.key, ...result, error: t('admin.users.rosterPaste.listedTwice') };
      }
      if (!result.error) seen.add(result.email);
      return { key: row.key, ...result };
    });
  }, [filled, t]);

  const errorFor = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of checked) if (c.error) map.set(c.key, c.error);
    return map;
  }, [checked]);

  const valid = checked.filter((c) => !c.error);
  const overCap = valid.length > MAX_BULK_INVITES;

  // Resolved at send time rather than seeded into state, so a catalogue that resolves late can't
  // overwrite a choice already made.
  const effectiveFederation = federationId || federations?.[0]?.id || '';
  const effectiveSport = sportId || sports?.[0]?.id || '';
  const tenantMissing = isSuper && (!effectiveFederation || !effectiveSport);

  function close() {
    setOpen(false);
    setRows(Array.from({ length: STARTING_ROWS }, emptyRow));
    setResults(null);
    setFederationId('');
    setSportId('');
    setInviteRole('referee');
  }

  function updateRow(key: string, patch: Partial<DraftRow>) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function removeRow(key: string) {
    // Never leave the form with nothing to type into.
    setRows((prev) => (prev.length === 1 ? [emptyRow()] : prev.filter((r) => r.key !== key)));
  }

  // Fill rows from pasted/CSV text. Existing filled rows are kept and the blanks are replaced, so
  // adding a second roster to a half-typed form doesn't discard either.
  function absorb(text: string) {
    const parsed = parseRosterPaste(text);
    if (parsed.length === 0) return;
    setRows((prev) => {
      const kept = prev.filter((r) => r.full_name.trim() || r.email.trim());
      const added = parsed.map((p) => ({ ...emptyRow(), full_name: p.full_name, email: p.email }));
      return [...kept, ...added, emptyRow()];
    });
  }

  // A multi-line paste into a single field means a spreadsheet column, not a name — expand it
  // rather than dropping a wall of text into one input.
  function onPaste(e: React.ClipboardEvent, key: string) {
    const text = e.clipboardData.getData('text');
    if (!text.includes('\n')) return;
    e.preventDefault();
    // Drop the row being pasted into if it's still empty, so the expansion doesn't leave a blank
    // above itself; otherwise keep what's already been typed there.
    setRows((prev) => prev.filter((r) => r.key !== key || r.full_name.trim() || r.email.trim()));
    absorb(text);
  }

  async function readCsv(file: File | undefined) {
    if (!file) return;
    try {
      absorb(await file.text());
    } catch {
      notifyError(t('admin.users.bulkInviteDialog.csvReadError'));
    }
    // Clear the input so picking the SAME file again still fires a change event.
    if (fileRef.current) fileRef.current.value = '';
  }

  async function send() {
    const payload: CreateInviteInput[] = valid.slice(0, MAX_BULK_INVITES).map((r) => ({
      full_name: r.full_name,
      email: r.email,
      role: inviteRole,
      ...(isSuper ? { federation_id: effectiveFederation, sport_id: effectiveSport } : {}),
    }));

    try {
      const result = await bulk.mutateAsync(payload);
      setResults(result.results);
      if (result.failed === 0) {
        notifySuccess(t('admin.users.bulkInviteDialog.sentToast', { count: result.created }));
      } else {
        notifyWarning(
          t('admin.users.bulkInviteDialog.partialToast', { created: result.created, total: result.results.length }),
        );
      }
    } catch (err) {
      notifyError(err, t('admin.users.bulkInviteDialog.errorToast'));
    }
  }

  return (
    <>
      <Button variant="default" size="sm" onClick={() => setOpen(true)}>
        <Icon.users className="h-3.5 w-3.5" /> {t('admin.users.bulkInviteDialog.trigger')}
      </Button>

      <Modal
        open={open}
        onClose={bulk.isPending ? () => {} : close}
        title={results ? t('admin.users.bulkInviteDialog.titleSent') : t('admin.users.bulkInviteDialog.titleForm')}
        sub={results ? t('admin.users.bulkInviteDialog.subSent') : t('admin.users.bulkInviteDialog.subForm')}
        panelClass="max-w-2xl"
        footer={
          results ? (
            <Button variant="accent" onClick={close}>
              {t('admin.users.inviteDialog.done')}
            </Button>
          ) : (
            <>
              <Button onClick={close} disabled={bulk.isPending}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="accent"
                onClick={send}
                disabled={bulk.isPending || valid.length === 0 || overCap || tenantMissing}
              >
                {bulk.isPending ? (
                  <>
                    <Spinner className="w-3.5 h-3.5" /> {t('admin.users.inviteDialog.sending')}
                  </>
                ) : valid.length === 0 ? (
                  t('admin.users.bulkInviteDialog.sendInvitesEmpty')
                ) : (
                  t('admin.users.bulkInviteDialog.sendInvites', { count: valid.length })
                )}
              </Button>
            </>
          )
        }
      >
        {results ? (
          <ResultList results={results} />
        ) : (
          <div className="flex flex-col gap-3.5">
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_2rem] gap-2 text-[11px] font-medium uppercase tracking-[0.05em] text-muted">
                <span>{t('admin.users.bulkInviteDialog.colFullName')}</span>
                <span>{t('admin.users.inviteDialog.emailLabel')}</span>
                <span />
              </div>
              {rows.map((row, i) => {
                const error = errorFor.get(row.key);
                return (
                  <div key={row.key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_2rem] items-start gap-2">
                    <Input
                      aria-label={t('admin.users.bulkInviteDialog.fullNameAria', { n: i + 1 })}
                      placeholder={t('admin.users.inviteDialog.fullNamePlaceholder')}
                      autoComplete="off"
                      value={row.full_name}
                      onChange={(e) => updateRow(row.key, { full_name: e.target.value })}
                      onPaste={(e) => onPaste(e, row.key)}
                    />
                    <div>
                      <Input
                        aria-label={t('admin.users.bulkInviteDialog.emailAria', { n: i + 1 })}
                        type="email"
                        placeholder={t('admin.users.bulkInviteDialog.emailPlaceholder')}
                        autoComplete="off"
                        value={row.email}
                        onChange={(e) => updateRow(row.key, { email: e.target.value })}
                        onPaste={(e) => onPaste(e, row.key)}
                      />
                      {/* The message sits under the row it belongs to — which person to fix, not
                          "something in your list is wrong". */}
                      {error && <p className="mt-1 text-[11px] text-danger">{error}</p>}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon
                      aria-label={t('admin.users.bulkInviteDialog.removePersonAria', { n: i + 1 })}
                      onClick={() => removeRow(row.key)}
                    >
                      <Icon.close className="h-3 w-3" />
                    </Button>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="default" size="sm" onClick={() => setRows((prev) => [...prev, emptyRow()])}>
                <Icon.plus className="h-3.5 w-3.5" /> {t('admin.users.bulkInviteDialog.addAnother')}
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv,text/plain"
                className="hidden"
                aria-label={t('admin.users.bulkInviteDialog.importCsvAria')}
                onChange={(e) => void readCsv(e.target.files?.[0])}
              />
              {/* Not a CSV interface — it fills these same fields, and you correct them here. */}
              <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
                {t('admin.users.bulkInviteDialog.importCsv')}
              </Button>
              <p className="text-[11px] text-muted">
                {t('admin.users.bulkInviteDialog.blankNameHint')}
              </p>
            </div>

            {overCap && (
              <p className="text-[12px] text-danger">
                {t('admin.users.bulkInviteDialog.overCapWarning', { max: MAX_BULK_INVITES })}
              </p>
            )}

            <div className={'grid gap-3.5 ' + (isSuper ? 'sm:grid-cols-3' : '')}>
              <Field label={t('admin.users.editDialog.roleLabel')} htmlFor="bulk-invite-role">
                <Select
                  id="bulk-invite-role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as Exclude<UserRole, 'super_admin'>)}
                >
                  <option value="referee">{t('admin.users.editDialog.roleReferee')}</option>
                  <option value="federation_admin">{t('admin.users.editDialog.roleFederationAdmin')}</option>
                </Select>
              </Field>
              {/* One role, one federation and one sport for the whole batch — a per-person role
                  would be a fourth column for something that varies once in a hundred rows. */}
              {isSuper && (
                <>
                  <Field label={t('admin.users.editDialog.federationLabel')} htmlFor="bulk-invite-federation">
                    <Select
                      id="bulk-invite-federation"
                      value={effectiveFederation}
                      onChange={(e) => setFederationId(e.target.value)}
                    >
                      {(federations ?? []).map((fed) => (
                        <option key={fed.id} value={fed.id}>{fed.name}</option>
                      ))}
                    </Select>
                  </Field>
                  <Field label={t('admin.users.editDialog.sportLabel')} htmlFor="bulk-invite-sport">
                    <Select
                      id="bulk-invite-sport"
                      value={effectiveSport}
                      onChange={(e) => setSportId(e.target.value)}
                    >
                      {(sports ?? []).map((sp) => (
                        <option key={sp.id} value={sp.id}>{sp.name}</option>
                      ))}
                    </Select>
                  </Field>
                </>
              )}
            </div>

            {!isSuper && me?.federation_name && (
              <p className="text-[12px] text-muted">
                <Trans
                  i18nKey="admin.users.bulkInviteDialog.joiningNote"
                  values={{ federation: me.federation_name }}
                  components={{ bold: <span className="font-medium text-ink-2" /> }}
                />
              </p>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

// The result of the send, one row per person. A partial success is normal, so this is a verdict
// list rather than a success screen — and an invite created whose email didn't go out is called
// out separately, since somebody has to share its link by hand.
function ResultList({ results }: { results: BulkInviteRowResult[] }) {
  const { t } = useTranslation();
  return (
    // `overflow-auto` rather than -y-auto: inside a phone-width modal the verdict table needs the
    // horizontal axis too, and the min-width below is what forces the scroll instead of a crush.
    <div className="max-h-80 overflow-auto rounded border border-line">
      <table className="w-full min-w-[440px] text-left text-[12.5px]">
        <tbody>
          {results.map((r, i) => (
            <tr key={`${r.email}-${i}`} className="border-b border-line last:border-0">
              <td className="px-3 py-1.5 font-medium text-ink">{r.full_name}</td>
              <td className="px-3 py-1.5 font-mono text-[12px] text-ink-2">{r.email}</td>
              <td className="px-3 py-1.5 text-right text-[11px]">
                {r.status === 'created' ? (
                  r.email_sent === false ? (
                    <span className="text-warn">{t('admin.users.bulkInviteDialog.resultCreatedEmailNotSent')}</span>
                  ) : (
                    <span className="text-success">{t('admin.users.bulkInviteDialog.resultSent')}</span>
                  )
                ) : (
                  <span className="text-danger">{r.message ?? t('admin.users.bulkInviteDialog.resultFailedFallback')}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
